import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import PromoLoader from '../PromoLoader/components/PromoLoader';
import FindOrder from '../Checkout/components/Checkout/FindOrder/FindOrder';
import { CheckoutOrderFragment, CheckinOrderFragment, useOrderQuery, useGetCheckinOrderQuery } from '@websky/graphql';
import { useRefuseExchangeMutation, ExareStatus } from '@websky/graphql';
import { isCheckinOrderResponse, isOrderResponse } from '../typeguards';

type Order = CheckoutOrderFragment | CheckinOrderFragment;

interface Props<T extends Order> {
	orderId: string;
	orderLoaded: boolean;
	onOrderLoad: (order: T) => void;
	onOrderAccessGranted: (order: T) => void;
	loadOrder: typeof useOrderQuery | typeof useGetCheckinOrderQuery;
	children?: React.ReactElement;
}

export default function OrderGuard<T extends Order>({
	children,
	onOrderLoad,
	loadOrder,
	orderId,
	orderLoaded,
	onOrderAccessGranted
}: Props<T>): React.ReactElement<Props<T>> {
	const [userHasAccess, setUserHasAccess] = useState(true);
	const { refetch, loading } = loadOrder({ skip: true, fetchPolicy: 'no-cache' });

	const [refuseExchange] = useRefuseExchangeMutation();

	const load = async () => {
		setUserHasAccess(true);

		try {
			const { data, errors } = await refetch({ id: orderId });

			if (errors) {
				throw errors;
			} else if (data && isOrderResponse(data)) {
				if (data.Order.exareInfo?.lastExchange?.status === ExareStatus.TimelimitExpired) {
					await refuseExchange({ variables: data.Order.exareInfo.lastExchange });
					const { data: order, errors } = await refetch();

					if (errors) {
						throw errors;
					}

					if (isOrderResponse(order)) {
						onOrderLoad(order.Order as T);
					}
				} else {
					onOrderLoad(data.Order as T);
				}
			} else if (data && isCheckinOrderResponse(data)) {
				onOrderLoad(data.CheckinOrder as T);
			} else {
				setUserHasAccess(false);
			}
		} catch (error) {
			console.error(error);
			setUserHasAccess(false);
		}
	};

	const orderAccessGrantedHandler = useCallback(
		(order: Order) => {
			onOrderAccessGranted(order as T);
			setUserHasAccess(true);
		},
		[onOrderAccessGranted]
	);

	useEffect(() => {
		if (!orderLoaded) {
			load();
		}
	}, [orderLoaded]);

	if (!userHasAccess) {
		return <FindOrder initialId={orderId} onSuccess={orderAccessGrantedHandler} />;
	}

	return !orderLoaded || loading ? <PromoLoader innerPromoLoader={true} /> : <>{children}</>;
}
